/**
 * useUserLookup — resolves user UUIDs to display names using project members.
 *
 * Returns a `resolve(userId)` function that returns the best available
 * display string: display_name > email > truncated UUID.
 */

import { useMemo } from "react";
import { useMembers } from "./useMembers";

function truncateUuid(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

export function useUserLookup(projectId: string | undefined) {
  const { data: members } = useMembers(projectId);

  const lookup = useMemo(() => {
    const map = new Map<string, string>();
    if (!members) return map;
    for (const m of members) {
      map.set(m.user_id, m.display_name || m.email || truncateUuid(m.user_id));
    }
    return map;
  }, [members]);

  return (userId: string | null | undefined): string => {
    if (!userId) return "—";
    return lookup.get(userId) ?? truncateUuid(userId);
  };
}
